import { Transition } from "@headlessui/react";
import React, { Fragment, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { ImCross } from "react-icons/im";
import { AiFillEye, AiFillEyeInvisible } from "react-icons/ai";

import { emailValidation, mediumPasswordValidation, strongPasswordValidation, stringSpaceValidation } from "helpers/validations";

import { checkEmailAvailabilityInServer, checkUsernameAvailabilityInServer, registerUser } from "actions/auth";
import { fetchingDataFromApi } from "actions/loaders";

import EmailOTPVerification from "views/components/auth/verification/EmailOTPVerification";

const DaffiUserRegister = ({ navigatePage, registrationType }) => {
	const dispatch = useDispatch();

	const [formData, setFormData] = useState(null);
	const [isUsernameAvailable, setIsUsernameAvailable] = useState(true);
	const [isEmailAvailable, setIsEmailAvailable] = useState(true);
	const [isUsernameValid, setIsUsernameValid] = useState(true);
	const [isEmailValid, setIsEmailValid] = useState(true);
	const [passwordStrength, setPasswordStrength] = useState(null);
	const [showPassword, setShowPassword] = useState(null);
	const [newlyRegisteredUserID, setNewlyRegisteredUserID] = useState(null);

	const fetchingDataFromApi = useSelector((state) => state.FetchingDataFromApi);

	const handleChange = (e) => {
		setFormData({
			...formData,
			[e.target.name]: e.target.value,
		});

		if (e.target.name === "email") setIsEmailValid(emailValidation.test(e.target.value));
		if (e.target.name === "username") setIsUsernameValid(stringSpaceValidation.test(e.target.value));
		if (e.target.name === "password") {
			if (strongPasswordValidation.test(e.target.value)) setPasswordStrength("strong");
			else if (mediumPasswordValidation.test(e.target.value)) setPasswordStrength("medium");
			else if (e.target.value) setPasswordStrength("weak");
			else setPasswordStrength(null);
		}
	};

	const handleSubmit = async () => {
		dispatch(fetchingDataFromApi(true));

		await registerUser(formData, registrationType).then(async (data) => {
			if (data?.userID) {
				setNewlyRegisteredUserID(data?.userID);
			}
		});

		dispatch(fetchingDataFromApi(false));
	};

	return (
		<Transition
			as={Fragment}
			appear={true}
			show={true}
			enter="transition-opacity duration-500"
			enterFrom="opacity-0"
			enterTo="opacity-100"
			leave="transition-opacity duration-250"
			leaveFrom="opacity-100"
			leaveTo="opacity-0"
		>
			<div className="flex-col w-full space-y-14">
				{newlyRegisteredUserID ? (
					<div className="w-full text-center text-sm text-gray-600 space-y-14">
						{newlyRegisteredUserID && (
							<EmailOTPVerification
								userID={newlyRegisteredUserID}
								formData={formData}
								registrationType={registrationType}
								navigatePage={navigatePage}
							/>
						)}
					</div>
				) : (
					<div className="flex-col w-full space-y-4">
						<div className="flex-col items-center justify-start">
							<p className={`flex font-medium text-dark-blue`}>Email</p>
							<div className="flex-col space-y-1 w-full">
								<input
									className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
									name="email"
									placeholder="Enter Your Email"
									type="email"
									required
									value={formData?.email ? formData.email : ""}
									onChange={async (e) => {
										handleChange(e);
										setIsEmailAvailable(await checkEmailAvailabilityInServer(e.target.value));
									}}
								/>
								{!isEmailAvailable && formData?.email?.length > 1 && (
									<div className="flex items-center space-x-2">
										<ImCross className="text-sm text-candy-red" />
										<p className="text-sm text-candy-red">Already taken</p>
									</div>
								)}
								{!isEmailValid && formData?.email?.length > 1 && (
									<div className="flex items-center space-x-2">
										<ImCross className="text-sm text-candy-red" />
										<p className="text-sm text-candy-red">Please enter a valid email</p>
									</div>
								)}
							</div>
						</div>
						<div className="flex-col items-center justify-start">
							<p className={`flex font-medium text-dark-blue`}>
								Give a name to your Daffi<span className="italic">Wallet</span>
							</p>
							<div className="flex-col space-y-1 w-full">
								<input
									className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
									name="username"
									placeholder="Create a username"
									type="text"
									required
									value={formData?.username ? formData.username : ""}
									onChange={async (e) => {
										handleChange(e);
										setIsUsernameAvailable(await checkUsernameAvailabilityInServer(e.target.value));
									}}
								/>
								{!isUsernameAvailable && formData?.username?.length > 1 && (
									<div className="flex items-center space-x-2">
										<ImCross className="text-sm text-candy-red" />
										<p className="text-sm text-candy-red">Already taken</p>
									</div>
								)}
								{!isUsernameValid && formData?.username?.length > 1 && (
									<div className="flex items-start space-x-2">
										<ImCross className="text-sm text-candy-red" />
										<p className="text-sm text-candy-red">Spaces are not allowed</p>
									</div>
								)}
							</div>
						</div>
						<div className="flex-col items-center justify-start">
							<p htmlFor="password" className={`flex font-medium text-dark-blue`}>
								Password
							</p>
							<label htmlFor="password" className="relative text-gray-400 focus-within:text-gray-600 block">
								{showPassword ? (
									<AiFillEyeInvisible
										className="absolute text-lg cursor-pointer top-1/2 transform -translate-y-1/2 right-3"
										onClick={() => setShowPassword(false)}
									/>
								) : (
									<AiFillEye
										className="absolute text-lg cursor-pointer top-1/2 transform -translate-y-1/2 right-3"
										onClick={() => setShowPassword(true)}
									/>
								)}
								<input
									name="password"
									placeholder="Password"
									type={showPassword ? "text" : "password"}
									required
									onChange={(e) => handleChange(e)}
									className="form-input appearance-none rounded-none block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
								/>
							</label>

							{passwordStrength && (
								<div className="flex w-full items-center space-x-2">
									{Array.from({ length: 5 }, (_, index) => (
										<div
											className={`w-6 h-2 ${
												passwordStrength === "weak" && index < 2
													? "bg-dark-blue"
													: passwordStrength === "medium" && index < 3
													? "bg-burned-orange"
													: passwordStrength === "strong" && index < 5
													? "bg-candy-red"
													: "bg-dark-blue bg-opacity-10"
											}`}
											key={index}
										></div>
									))}
									<p
										className={`text-sm font-bold ${
											passwordStrength === "weak"
												? "text-dark-blue"
												: passwordStrength === "medium"
												? "text-burned-orange"
												: "text-candy-red"
										}`}
									>
										{passwordStrength}
									</p>
								</div>
							)}
							{/* {!passwordStrength && formData?.password?.length > 1 && (
								<div className="flex items-start space-x-2">
									<ImCross className="text-sm text-candy-red" />
									<p className="text-sm text-candy-red">
										Password Must contain at least <br />
										1 lowercase alphabetical character <br />
										1 uppercase alphabetical character <br />
										1 numeric character <br />
										1 special character ._!@#$%^&* <br />
										length must be &gt; 8
									</p>
								</div>
							)} */}
						</div>

						<div className="flex w-full items-center justify-center">
							{fetchingDataFromApi ? (
								<img className="animate-spin mx-auto h-5 w-auto" src="/media/logo.png" alt="img" />
							) : (
								<button
									onClick={() => handleSubmit()}
									disabled={
										formData?.email &&
										formData?.username &&
										formData?.password &&
										isEmailAvailable &&
										isUsernameAvailable
											? false
											: true
									}
									className="bg-dark-blue smooth hover:text-black hover-border-gray-900 text-gray-100 py-3 px-8 mt-5 border border-transparent rounded-3xl disabled:bg-gray-100 disabled:text-gray-400"
								>
									Create
								</button>
							)}
						</div>
					</div>
				)}
			</div>
		</Transition>
	);
};

export default DaffiUserRegister;
